import type { en } from './en';

/**
 * 简体中文。Values are copied **verbatim** from Android's
 * `values-zh-rCN/strings.xml` — not re-translated, so the same operator reading
 * both apps sees the same wording. 术语沿用 Android 官方词汇表，按钮用简短祈使句，
 * 错误信息用完整句子;the WebClip-only block at the bottom follows the same register.
 *
 * `satisfies Record<keyof typeof en, string>` makes a missing key a build error.
 */
export const zhCN = {
  app_name: 'CheckLinks',

  nav_dashboard: '概览',
  nav_results: '结果',
  nav_settings: '设置',

  common_see_all: '查看全部',
  common_cancel: '取消',
  value_none: '—',
  value_unknown: '未知',
  unit_ms: '{0} 毫秒',

  net_offline: '已断开',
  net_no_connection_body: '无网络连接 — 设备无法检测链接。',

  home_progress_label: '进度',
  home_percent: '{0}%',
  home_checked_of_total: '{0} / {1}',
  home_waiting_network: '无网络连接，正在等待。恢复后会自动继续。',
  home_round: '第 {0}/{1} 轮 — {2} 个链接尚未通过',
  home_next_round: '第 {0} 轮将在 {1} 后开始',
  home_recent_label: '最近',
  home_start: '开始检测',
  home_run_again: '重新检测',
  home_stop: '停止检测',
  home_view_results: '查看结果',
  stat_alive: '正常',
  stat_dead: '失效',
  stat_response: '响应',

  err_generic_title: '出现错误',
  err_try_again_later: '请稍后重试。',
  err_key_invalid: '设备密钥无效或已被禁用。',
  err_key_required: '请输入设备密钥。',
  err_server_unreachable: '无法连接服务器，请检查网络。',

  a11y_change_language: '切换语言',
  a11y_copy: '复制',

  settings_language: '语言',
  settings_section_language: '语言',
  settings_measurement: '测量方式',
  settings_measurement_value: '{0} 轮，间隔 {1} 秒 — 由服务器设定',
  settings_measurement_unknown: '尚未从服务器获取',
  settings_section_device: '设备',
  device_model: '设备型号',
  device_app_version: '应用版本',
  device_network: '当前网络',
  device_carrier: '运营商',
  device_region: '地区',
  device_ip: 'IP 地址',
  device_summary: '{0} · v{1}',

  filter_all: '全部 {0}',
  filter_alive: '正常 {0}',
  filter_dead: '失效 {0}',
  result_empty: '没有链接',
  result_copied: '已复制',

  keyentry_subtitle: '输入已发放的设备密钥以开始',
  keyentry_field_label: '设备密钥（dvk_…）',
  keyentry_submit: '验证并保存',

  settings_section_device_key: '设备密钥',
  device_key_remove: '移除本机的设备密钥',
  device_key_remove_title: '移除本机的设备密钥？',
  device_key_remove_body:
    '本机保存的密钥将被删除。定时检测会停止，自动运行也会关闭。需要重新输入密钥才能配置这台设备。',
  device_key_remove_action: '移除密钥',
  device_key_removing: '正在移除密钥…',
  device_key_removing_body: '正在停止仍在运行的检测。',

  // --- WebClip-only, translated here (no Android source to copy from) ---------

  ios_net_online: '已连接',

  ios_last_check_label: '最近一次检测',
  ios_last_check_never: '尚未上报任何数据',
  ios_last_check_ago: '{0}上报',
  ios_last_check_at: '于 {0}',
  ios_last_check_stale:
    '本机已超过 {0} 分钟没有动静。请检查：屏幕是否熄灭、应用是否被划掉、手机是否刚刚重启。',
  ios_next_run: '{0} 后开始下一轮',
  ios_next_run_none: '未安排下一轮',
  ios_running_now: '正在进行一轮检测…',

  ios_ago_just_now: '刚刚',
  ios_ago_seconds: '{0} 秒前',
  ios_ago_minutes: '{0} 分钟前',
  ios_ago_hours: '{0} 小时 {1} 分钟前',
  ios_ago_days: '{0} 天 {1} 小时前',

  ios_batch_saved: '最近一批：已保存 {0} 条',
  ios_batch_saved_skipped: '最近一批：已保存 {0} 条，跳过 {1} 条',
  ios_batch_dropped: '{0} 个链接未跑满所有轮次 — 已刻意不上报',

  ios_phase_fetching: '正在获取配置和链接列表…',
  ios_phase_submitting: '正在提交结果…',

  // 全角问号是刻意的：问号属于译文本身，不是代码后面拼上去的半角 "?" —
  // 见 en.ts 的说明（SPEC §13）。用词与 `ios_caveat` 里解释的“正常”保持一致。
  ios_status_alive_maybe: '正常？',

  ios_status_unmeasurable: '无法测量',
  ios_error_mixed_content: '纯 http:// 域名 — Safari 阻止混合内容。这不是对该链接的判定。',

  ios_key_revoked_title: '设备密钥被拒绝',
  ios_key_revoked_body: '服务器返回 401 — 该密钥已过期或设备已被禁用。应用已彻底停止，不会重试。',
  ios_key_revoked_hint:
    '密钥保存在图标的网址里，无法在应用内更改。请向管理员索取新的安装链接，然后删除旧图标并重新“添加到主屏幕”。',

  ios_settings_section_operation: '运行',
  ios_settings_schedule: '计划',
  ios_settings_schedule_value: '每 {0} 分钟一次 — 由服务器设定',
  ios_settings_schedule_default: '每 {0} 分钟一次 — 默认值，未能读取服务器配置',
  ios_settings_autorun_off: '服务器已关闭自动检测。本机仍会定时唤醒并询问，以便随时远程重新开启。',
  ios_settings_foreground_only:
    '本应用只在屏幕点亮且 WebClip 处于前台时运行，重启后不会自动打开。这是平台限制，不是故障。',

  ios_settings_section_device_setup: '必要的设备设置',
  ios_setup_autolock: '设置 → 显示与亮度 → 自动锁定：永不',
  ios_setup_lowpower: '关闭低电量模式',
  ios_setup_charger: '保持连接充电器',
  ios_setup_guided_access: '开启引导式访问，锁定在本 WebClip 上',
  ios_setup_note: '应用无法检查或修改以上任何一项 — Safari 没有访问权限。',

  ios_device_platform: '平台',
  ios_device_user_agent: '用户代理',

  ios_caveat:
    '这里的“正常”意思是“可能正常”，而不是“已确认正常”。本应用只能测出某个地址是否可达；它读不到状态码，也分不清真实网站和运营商的拦截页。请不要把这些数字与 Android 应用的数据放在同一置信度下汇总。',

  ios_copy_failed: '无法复制',
} satisfies Record<keyof typeof en, string>;
